import { useCallback, useEffect, useMemo, useState } from "react";
import { useTrailSave, TrailSaveProgress } from "@/lib/useTrailSave";
import { Polyline, CircleMarker, Tooltip } from "react-leaflet";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import BusinessLayers, { LAYER_META } from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Route, Ruler, Layers, Utensils, Bike, MapPin, Save, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// Compact read-only trail preview — same map experience as the Discover Trails
// tab (route line, start/end, nearby businesses) shown in a popup window.
export default function TrailMapDialog({ trailId, open, onOpenChange, onSaved }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [bizStatus, setBizStatus] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [layers, setLayers] = useState({ restaurant: true, bike_shop: true, coffee: false, brewery: false, hotel: false });
  const [heatOn, setHeatOn] = useState(false);
  const [bufferMiles, setBufferMiles] = useState("1");

  useEffect(() => {
    if (!open || !trailId) { setSelected(null); setBusinesses([]); setBizStatus(null); return undefined; }
    let cancelled = false;
    setLoading(true); setSelected(null); setBusinesses([]); setBizStatus(null); setHeatOn(false); setBufferMiles("1");
    api.get(`/admin/trails/${trailId}`)
      .then(({ data }) => { if (!cancelled) setSelected(data); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => { if (!cancelled) setLoading(false); });
    setBizLoading(true);
    api.get(`/admin/trails/${trailId}/businesses`, { params: { buffer_miles: 1 } })
      .then(({ data }) => { if (!cancelled) { setBusinesses(data.businesses || []); setBizStatus(data.osm_status); } })
      .catch(() => { if (!cancelled) setBizStatus("unavailable"); })
      .finally(() => { if (!cancelled) setBizLoading(false); });
    return () => { cancelled = true; };
  }, [open, trailId]);

  const lines = selected?.lines_latlng || [];
  const bounds = lines.length ? lines.flat() : null;
  const visibleBiz = useMemo(() => businesses.filter((b) => layers[b.layer]), [businesses, layers]);
  const bizCounts = useMemo(() => {
    const c = { restaurant: 0, bike_shop: 0, coffee: 0, brewery: 0, hotel: 0 };
    businesses.forEach((b) => { if (c[b.layer] != null) c[b.layer] += 1; });
    return c;
  }, [businesses]);
  const heatPoints = useMemo(() => businesses.filter((b) => b.lat != null && b.lng != null).map((b) => [b.lat, b.lng, 0.6]), [businesses]);

  const trailSave = useTrailSave(useCallback((result) => {
    setSelected((s) => ({ ...s, status: "saved", saved_bike_path_id: result?.bike_path_id }));
    if (onSaved) onSaved();
  }, [onSaved]));
  const saving = trailSave.saving;
  const saveTrail = () => { if (selected) trailSave.save(selected.id, bufferMiles); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="trail-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Route className="w-5 h-5 text-primary" />{selected?.name || "Trail preview"}</DialogTitle>
        </DialogHeader>

        <div className="relative rounded-xl overflow-hidden border border-border">
          <LeafletMap height={420}>
            {bounds && <FitBounds bounds={bounds} nonce={selected?.id} />}
            {lines.map((ln, i) => <Polyline key={i} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 5 }} />)}
            {selected?.start_point && <CircleMarker center={[selected.start_point.lat, selected.start_point.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#22c55e", fillOpacity: 1 }}><Tooltip>Start</Tooltip></CircleMarker>}
            {selected?.end_point && <CircleMarker center={[selected.end_point.lat, selected.end_point.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#ef4444", fillOpacity: 1 }}><Tooltip>End</Tooltip></CircleMarker>}
            {visibleBiz.length > 0 && <BusinessLayers businesses={visibleBiz} readOnly />}
            {heatOn && <HeatLayer points={heatPoints} enabled={heatOn} />}
          </LeafletMap>

          {loading && <div className="absolute inset-0 z-[1000] bg-background/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

          {selected && (
            <div className="absolute bottom-3 left-3 z-[1000] flex flex-wrap gap-1.5 max-w-[calc(100%-1.5rem)]" data-testid="trail-map-dialog-toggles">
              {Object.entries(LAYER_META).map(([k, m]) => (
                <button key={k} type="button" onClick={() => setLayers((l) => ({ ...l, [k]: !l[k] }))} data-testid={`dialog-biz-toggle-${k}`}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${layers[k] ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}>
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color, opacity: layers[k] ? 1 : 0.35 }} />
                  {m.label}<span className="opacity-60">{bizCounts[k] || 0}</span>
                </button>
              ))}
              <button type="button" onClick={() => setHeatOn((v) => !v)} data-testid="dialog-biz-toggle-heat"
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] backdrop-blur transition-colors ${heatOn ? "bg-card/95 border-border text-foreground" : "bg-card/60 border-border/50 text-muted-foreground"}`}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "linear-gradient(90deg,#22d3ee,#facc15,#ef4444)", opacity: heatOn ? 1 : 0.4 }} />
                Heatmap
              </button>
              {bizLoading && <span className="flex items-center gap-1.5 rounded-md border border-border bg-card/90 px-2 py-1 text-[11px]" data-testid="dialog-biz-loading"><Loader2 className="w-3 h-3 animate-spin" />Loading businesses…</span>}
              {!bizLoading && bizStatus === "unavailable" && <span className="rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] text-amber-300" data-testid="dialog-biz-unavailable">Business data temporarily unavailable</span>}
            </div>
          )}
        </div>

        {selected && (
          <div className="grid grid-cols-2 gap-2 mt-1 text-xs" data-testid="trail-map-dialog-stats">
            <div className="flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5 text-primary" />{selected.length_miles} miles</div>
            <div className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-primary" />{selected.surface_class || "Unknown"}</div>
            <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-2" data-testid="dialog-trail-restaurants">
              <div className="text-lg font-heading font-extrabold text-orange-300 flex items-center gap-1"><Utensils className="w-4 h-4" />{selected.restaurant_count ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">restaurants nearby · {selected.restaurants_per_mile ?? 0}/mi</div>
            </div>
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2" data-testid="dialog-trail-bikeshops">
              <div className="text-lg font-heading font-extrabold text-cyan-300 flex items-center gap-1"><Bike className="w-4 h-4" />{selected.market_bike_shop_count ?? 0}</div>
              <div className="text-[10px] text-muted-foreground">bike shops nearby · {selected.market_bike_shops_per_mile ?? 0}/mi</div>
            </div>
            <div className="col-span-2 flex items-center gap-4 text-[11px]">
              <span><b className="text-emerald-300">{selected.partner_shop_count ?? 0}</b> partner shops</span>
              <span><b className="text-amber-300">{selected.prospect_shop_count ?? 0}</b> prospect shops</span>
              {selected.status === "saved" && <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Saved to Bike Paths</Badge>}
            </div>
            <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground"><MapPin className="w-3.5 h-3.5 text-primary" />{selected.cities?.length ? selected.cities.join(", ") : "—"}</div>
          </div>
        )}

        {selected && (
          <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="dialog-save-row">
            {selected.status === "saved" ? (
              <Button variant="outline" disabled className="flex-1" data-testid="dialog-trail-saved-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Saved as draft</Button>
            ) : (
              <>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">Corridor width</span>
                <Select value={bufferMiles} onValueChange={setBufferMiles}>
                  <SelectTrigger className="h-8 text-xs w-28" data-testid="dialog-trail-buffer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">½ mile</SelectItem>
                    <SelectItem value="1">1 mile</SelectItem>
                    <SelectItem value="1.5">1.5 miles</SelectItem>
                    <SelectItem value="2">2 miles</SelectItem>
                    <SelectItem value="3">3 miles</SelectItem>
                    <SelectItem value="5">5 miles</SelectItem>
                  </SelectContent>
                </Select>
                <Button className="flex-1" onClick={saveTrail} disabled={saving} data-testid="dialog-trail-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save to Bike Paths (draft)
                </Button>
              </>
            )}
          </div>
        )}
        <TrailSaveProgress job={trailSave.job} />
      </DialogContent>
    </Dialog>
  );
}
